import { ComponentType, createElement, useEffect } from 'react';
import lift from '@tcc/load-lifter';
import { DD } from '@tcc/analytics';
import { recordAppTotalLoadOnce } from './appTotalLoad';

type importFromS3Signature = (
  moduleName: string
// eslint-disable-next-line @typescript-eslint/no-explicit-any
) => Promise<{ default: ComponentType<any> }>;

const PLUGIN_LOAD_VITAL = 'plugin_load';

// Wraps a lazily-loaded plugin so it reports a `plugin_load` Datadog RUM duration
// vital once it mounts. This measures Stage 6 of startup — from the moment
// React.lazy invokes importFromS3 (bundle fetch begins) until the plugin first
// mounts. Tagged with the plugin name so each plugin can be compared. Gated on an
// available RUM instance, and reported at most once (first mount).
const withPluginLoadVital = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Plugin: ComponentType<any>,
  moduleName: string,
  startTime: number,
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): ComponentType<any> => (props) => {
  useEffect(() => {
    DD.addDurationVital(PLUGIN_LOAD_VITAL, {
      startTime,
      duration: Date.now() - startTime,
      context: { plugin: moduleName },
      description: 'Time to fetch and mount a plugin bundle',
    });
    // Also close the end-to-end app_total_load vital on the first plugin mount
    // (the landing route). Fires at most once per page load.
    recordAppTotalLoadOnce(moduleName);
    // Report once on the plugin's first mount; startTime/moduleName are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createElement(Plugin, props);
};

export const importFromS3: importFromS3Signature = (moduleName) =>
  new Promise((resolve) => {
    const startTime = Date.now();
    lift([moduleName], (module) =>
      resolve({ default: withPluginLoadVital(module.default, moduleName, startTime) })
    );
  });
